import endpointMapper from '../_endpoints'
import { APPLICATION_BEHAVIOUR } from '../_enums'
import { usePost } from '../_hooks/usePost'
import { useDispatch, useSelector } from 'react-redux'
import { addApp, removeApp } from '../_redux/actions/appsActions'
import { useFetch } from '../_hooks/useFetch'
import { ARC_ROLE_REQUEST_TITLE } from '../configs/constants'
import { toast } from 'react-toastify'
import ToastrComponentArcRoles from '../views/_molecules/ToastrComponentArcRequest'
import { useRef } from 'react'

export const useAppsService = () => {
  const userInfoRaw = localStorage.getItem('userInfoData')
  let sid
  try {
    sid = JSON.parse(userInfoRaw || '{}')?.sid
  } catch {
    sid = undefined
  }
  const [addingApplication, postAddApp] = usePost()
  const [removingApplication, postRemoveApp] = usePost()
  const [, grantAccessApp] = useFetch()
  const [, getWorkspaceApps] = useFetch()
  const [, postArcRequest] = usePost()
  const [loadingApplication, getApplicationById] = useFetch()
  const [loadingDraft, getDraftById] = useFetch()
  const { givenName } = useSelector((state) => state?.profileReducer?.profile)

  const dispatch = useDispatch()
  const userSid = useSelector((state) => state.userContextReducer.userContext.id)

  const addApplication = async (item) => {
    const response = await postAddApp({
      url: endpointMapper.userApplications.createUserApplication,
      data: {
        userSid,
        applicationId: item.id || item.applicationId,
        eventType: APPLICATION_BEHAVIOUR.ADD_APPLICATIONS
      }
    })

    if (response) {
      dispatch(addApp(item))
    }
    return response
  }

  const removeApplication = async (item) => {
    const response = await postRemoveApp({
      url: endpointMapper.userApplications.deleteUserApplication,
      data: {
        userSid,
        applicationId: item.id || item.applicationId,
        eventType: APPLICATION_BEHAVIOUR.ADD_APPLICATIONS
      }
    })
    dispatch(removeApp(item))
    return response
  }

  const checkIfUserHasAccess = async (appId) => {
    const result = await grantAccessApp(
      `${endpointMapper.bff.getADGroupsAssignedToUserByAppId}?appId=${appId}`
    )
    return result
  }

  const raiseArcRequest = async (req) => {
    const result = await postArcRequest({
      url: endpointMapper.bff.raiseArcRequest,
      data: req
    })
    return result
  }

  const getApplication = async (id) => {
    const result = await getApplicationById(
      endpointMapper.applications.getApplication.replace(':id', id)
    )
    return result
  }

  const getDraft = async (id) => {
    const result = await getDraftById(endpointMapper.applications.getDraft.replace(':id', id))
    return result
  }

  const toastId = useRef(null)
  const closeToastr = () => toast.dismiss(toastId.current)

  const sendArcRequest = async ({ application, selectedArcRoles, appAccess }) => {
    const endpoint = endpointMapper.userApplications.get
      .replace(':userId', sid)
      .replace(':eventType', APPLICATION_BEHAVIOUR.ADD_APPLICATIONS)

    const workspaceApps = await getWorkspaceApps(endpoint)

    const foundApp = workspaceApps?.data?.find((appWorkspace) => appWorkspace.id === application.id)
    try {
      const response = await raiseArcRequest({
        title: `${ARC_ROLE_REQUEST_TITLE} ${application?.name} (Requested by ${givenName})`,
        uniqueName: application?.uniqueName,
        arcRoles:
          selectedArcRoles && selectedArcRoles?.length && selectedArcRoles instanceof Array
            ? selectedArcRoles
                .map((role) => (typeof role === 'string' ? role : role?.value))
                .filter(Boolean)
            : appAccess?.arcRoles
      })
      if (response && response.status !== 'Success' && response.status !== 200) {
        toast.error(`An error occured while granting access to application ${application?.name}`)
      } else {
        const content = !foundApp
          ? toast.warning(
              <ToastrComponentArcRoles
                application={application}
                label="Access requested"
                main="It seems like you didn't add this app to your workspace. Do you want to add it now?"
                onClose={closeToastr}
              />
            )
          : null
      }
      return response
    } catch (error) {
      console.error(error)
    }
  }

  return {
    addingApplication,
    addApplication,
    removingApplication,
    removeApplication,
    checkIfUserHasAccess,
    raiseArcRequest,
    getApplication,
    loadingApplication,
    getDraft,
    loadingDraft,
    sendArcRequest
  }
}
